import {
  EnvironmentProviders,
  importProvidersFrom,
  makeEnvironmentProviders,
} from '@angular/core';
import { WafService } from './waf.service';
import { DialogModule } from '@angular/cdk/dialog';
import { WAF_CONFIG, WafConfig } from './waf.token';

/**
 * Sets up the needed providers for WAF.
 *
 * It works a lot like setting up providers in unit tests and old school
 * modules.  You can checkout how `provideHttpClient` works in Angular.
 * https://github.com/angular/angular/blob/b250bb0a74dad7b4a90b272db30215dc02abc761/packages/common/http/src/provider.ts#L106-L148
 *
 * I do this so that the interceptor will have the service provided.
 *
 * @param configFactory The WAF configuration. See parts below.
 *
 * `scriptUrl`: The URL of the WAF script to be loaded.
 * This is different depending on the environment.
 *
 * There are 2 script that need to be loaded, the challenge and the captcha.
 * While the captcha script will load the challenge script if not present,
 * it does so after the captcha is loaded.  This results in not knowing the
 * script is loaded from the `onLoad` callback.  Thus, we need to load both.
 *
 * In the aws console, waf > application integration > intellegent threat
 * the integration url is given.
 *
 * Dev: https://ab7482c3ed70.us-east-1.sdk.awswaf.com/ab7482c3ed70/730edefb35b3/
 *
 * The base URL is the same for both the challenge and captcha with the last url segment
 * removed from the challenge URL.
 *
 *
 * `token`: The token used to identify the WAF service for the captcha.  The allowed urls are `sight-sound.com`
 *
 * Dev: KVjTbzUJk754pIw/ZfXXd2sw6icQ/2oWjvnKs334p1jTOvr6L5HgaxUWaRViBls2bufDT/pG2SVIHaiMVYRR3Ks0OEjfY1jnGxNDGlPBQ9lWaXSDAYpnBTu8h8+v/PfZmXk2tLGZowk/A/h3zYpz8LRTOcYr5y07whThmbs1m+o2GCrRrV6DLPd4wcn1S2XF+oPG4wvYrAZHgEa8c4tBSyz1WEm+98FBGZBjpFSja9I7gofPUrpmgz9sKUbYi3/K930jghTcr3ugT78lKvuQXHLlioTz8LhCr2To75jCz7OnAXvxnMJdXH64jU/ggt8ms4Joxmq3uO4vUoEl7BMP4F5T4xkiMEwQEVU8glKBSDdB0COJuhkd4WJl18HoQqQOIS9lnJT0+NQ1+ioN0r3E2zxk08Qi/LBTNuF67iGvxlFYcKlAihniuDpWGo8cox04F7IKNqOoxoMdo8nYBuxrOUWNM7sHO6wRb0wNGeUXen35Mjg75KeBoZ5uKC4D17XgMDVDuRKNLBvwZl4NzaahNSG5zAUOcF+/l4800Z52yjGnwjuNU2bBBfxiQAs32AJa+0tgMuQhPoXrNnQwyfhErm1Ut+N3s4s6DaXt8YT73TYjTghJo0rurF/y8DNDgHmh8zhakzUJWil6gd9ieZP+fCVMw/rmmQPuOTVSkKT4nVg=_0_1
 * Prod: MDx8KomYN+wpX6Tw0oXdAbiE3YEptsUWCLs68rgpObdBir/BZ+Grl0T7yV7iycxqNpSCVJ6eRl7cERrmpNT2a5srE/vZiryaOVMeszd/I3oozKD5Obx8yxfEx4/ltflWUJ1fbKKujoU6kuABTJsj5VhcQqtulHXopv+ldEf2e9nNSmMcYzelauRFZt+nHkK1TDXfh2DhU2/pUq/HlhE6LoiHKNwIoK3H6vCLqWGIpmH6tE/fag1WRDj/ROEWaypSCjzYjt16p0TrH9tZX2Sa+8eyYT4xEcg99ReNCwnskIFSot8XF8ykHY5v96KUvLcWWkq+hN/HW6+dQnKVhaCth+lWeNrRWygp1G76WNuQ12i80iZnVpkVA8TytOYuKLMm2EjTJD/6d+NyufJI1PBQ8YZ+KMUFA0vD5dEbAnqBGzEe/E0taIWq9nMLl2Zirvc/Ta6hCI87dGbrthO1vBlJEFgBqQWr70ihyOF1worIXKlb1N9wHCtux9NdjvCATEEhuhn/dS0YmWO92z9fW3PtUjhAj5oF10obxyA3cKKSE1+wTDeNMMj5M3roISXI5l93ytJbdj/uQDDUmP7eJuZKplR3E9TKnqWeywkxt65hAaYo0yKvnWm1QkiRux/R9lbGKKlcUpg8w7+Sx049cWXsq2LnKFpaayvYdPgg2EMXq1Y=_0_1
 *
 * `apiUrl`: The base url the interceptor add the waf response to.  Most apps already have a `heliosUrl` in
 * the config to use for this.
 */
export const provideWaf = (
  configFactory: () => WafConfig
): EnvironmentProviders => {
  return makeEnvironmentProviders([
    {
      provide: WAF_CONFIG,
      useFactory: configFactory,
    },
    WafService,
    importProvidersFrom(DialogModule),
  ]);
};
